import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import classnames from 'classnames';

// Selectors
import * as OnboardingStateSelectors from 'Shared/selectors/OnboardingState.selectors';

// Actions
import { updateOnboardingState, updateTopicsInRedux } from 'Shared/actions/onboardingState';

// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton } from 'Wpds/components/button';
import AgendaItem from './AgendaItem';
import WpLabelled from 'Wpds/components/labelled/WpLabelled';
import WpLabel from 'Wpds/components/label/WpLabel';
import Spinner from 'Shared/components/Spinner';

// Styles
import './AgendaList.scss';

type Props = {
  footer: React.ReactNode;
  error: string;
};

const AgendaList = ({ footer, error }: Props) => {
  const dispatch = useDispatch();

  const topics = useSelector(state => OnboardingStateSelectors.getDiscussionTopics(state));

  const [showSaved, setShowSaved] = useState(false);

  const handleToggleShowSaved = () => {
    setShowSaved(false);
    setTimeout(() => {
      setShowSaved(true);
    }, 1000);
  };

  useEffect(() => {
    handleToggleShowSaved();
  }, [topics]);

  const makeUpdate = data => {
    handleToggleShowSaved();
    dispatch(updateOnboardingState(data));
  };

  const debounceUpdate = useRef(
    debounce(
      data => {
        makeUpdate(data);
      },
      500,
      { leading: false, trailing: true }
    )
  );

  const handleUpdateItem = useCallback(
    (index, value) => {
      const newTopics = topics;
      newTopics[index] = value;
      dispatch(
        updateTopicsInRedux({
          discussionTopics: newTopics,
        })
      );

      debounceUpdate.current({
        onboardingState: {
          discussion_topics: newTopics,
        },
        shouldUpdateRedux: false,
      });
    },
    [topics]
  );

  const handleRemoveClick = useCallback(
    index => {
      const newTopics = topics;
      newTopics.splice(index, 1);

      makeUpdate({
        onboardingState: {
          discussion_topics: newTopics,
        },
      });
    },
    [topics]
  );

  const handleAddNewClick = useCallback(() => {
    makeUpdate({
      onboardingState: {
        discussion_topics: [...topics, ''],
      },
    });
  }, [topics]);

  const baseClassName = 'AgendaList';

  const containerClasses = classnames(`${baseClassName}__container`, error && `${baseClassName}__container--error`);

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__agenda-header`}>
        <h5>Meeting Agenda for your 1:1&apos;s</h5>
        {showSaved ? (
          <div>
            <WpLabel id="saved-label">Saved</WpLabel>
          </div>
        ) : (
          <div className={`${baseClassName}__agenda-header__spinner`}>
            <Spinner color="#9498A4" activeIf spinnerStyles={{ width: '14px', height: '14px' }} />
          </div>
        )}
      </div>
      <WpLabelled id="agendaList" label="Agenda List" labelHidden error={error}>
        <WpContainer className={containerClasses}>
          <WpContainerBody>
            <div className={`${baseClassName}__list`}>
              {topics.map((topic, index) => {
                return (
                  <AgendaItem
                    topic={topic}
                    index={index}
                    handleChange={handleUpdateItem}
                    handleRemove={handleRemoveClick}
                    key={`${index}--onboarding-topic`}
                  />
                );
              })}
            </div>
            <WpButton variant="tertiary" onClick={handleAddNewClick}>
              <WpIcon name="plus-circle" />
              Add topic...
            </WpButton>
          </WpContainerBody>
        </WpContainer>
      </WpLabelled>
      {footer}
    </div>
  );
};

export default AgendaList;
